import { CloseSquareOutlined } from '@/assets/icons';
import {
	AutoComplete,
	Checkbox,
	DatePicker,
	Input,
	Select,
	SelectMasterInfo,
} from '@/components';
import { getFieldDateProps } from '@/utils';
import { ForwardedRef, forwardRef, memo, useCallback } from 'react';

import { Space } from 'antd';

import { isNilOrEmpty } from 'ramda-adjunct';

import Button from '../button';
import CheckboxGroup from '../checkbox-group';
import FilterDate from './filter-date';
import FilterTime from './filter-time';
import InputCheckbox from './input-checkbox';
import InputTarget from './input-target';
import useLogic from './logic';
import {
	EEditableFilterType,
	ISearchFilter,
	ISearchFilterRef,
	resolveFilterItem,
	TRootOptions,
} from './type';
const SearchFilter = (
	props: ISearchFilter,
	ref: ForwardedRef<ISearchFilterRef>
) => {
	const {
		labelClassName = '!min-w-[180px] font-normal',
		containerClassName = 'gap-2.5 xl:gap-x-2.5',
		hideAdditionalFilter = false,
	} = props;
	const {
		t,
		formik,
		state,
		onSearch,
		handleAddFilter,
		handleRemoveFilter,
		onClickReset,
	} = useLogic(props, ref);

	const renderItem = (item: TRootOptions) => {
		const { label, value, filterItem: fI } = item;
		const filterItem = resolveFilterItem(fI, formik.values);
		const getAdditionalValues = filterItem?.getAdditionalValues;

		switch (filterItem?.type) {
			case EEditableFilterType.TEXT_INPUT:
				return (
					<Input
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						inputClassName="!w-[200px]"
						name={value}
						value={formik.values[value]}
						onChange={(e) =>
							formik.setValues((prev: any) => ({
								...prev,
								[value]: filterItem.inputProps?.formatInput
									? filterItem.inputProps?.formatInput(
											e.target.value,
											filterItem.inputProps?.maxLength
										)
									: e.target.value,
								...(getAdditionalValues
									? getAdditionalValues?.(e, prev) ?? {}
									: {}),
							}))
						}
						onPressEnter={onSearch}
						{...filterItem.inputProps}
					/>
				);
			case EEditableFilterType.DROPDOWN:
				return (
					<SelectMasterInfo
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						className="!w-[200px]"
						name={value}
						value={formik.values[value] ?? ''}
						onChange={(e, op) =>
							formik.setValues((prev: any) => ({
								...prev,
								[value]: e,
								...(getAdditionalValues
									? getAdditionalValues?.(e, prev, op) ?? {}
									: {}),
							}))
						}
						onSelect={onSearch}
						{...filterItem.dropdownProps}
					/>
				);
			case EEditableFilterType.DATE_RANGE_PICKER:
				return (
					<FilterDate
						t={t}
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						name={value}
						formik={formik}
						onSearch={onSearch}
						{...filterItem.dateProps}
					/>
				);
			case EEditableFilterType.TIME_PICKER:
				return (
					<FilterTime
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						name={value}
						formik={formik}
						onSearch={onSearch}
					/>
				);
			case EEditableFilterType.DATE:
				return (
					<DatePicker
						{...getFieldDateProps(formik, value, filterItem.dateProps?.option)}
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						inputClassName="!w-[200px]"
					/>
				);
			case EEditableFilterType.CHECKBOX:
				return (
					<Checkbox
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						name={value}
						checked={formik.values[value]}
						onChange={(e) =>
							formik.setValues((prev) => ({
								...prev,
								[value]: e.target.checked,
								...(getAdditionalValues
									? getAdditionalValues?.(e, prev) ?? {}
									: {}),
							}))
						}
						onClick={onSearch}
						{...filterItem.checkboxProps}
					/>
				);
			case EEditableFilterType.CHECKBOX_GROUP:
				return (
					<CheckboxGroup
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						name={value}
						value={formik.values[value]}
						onChange={async (e) => {
							await formik.setValues((prev) => ({
								...prev,
								[value]: e,
								...(getAdditionalValues
									? getAdditionalValues?.(e, prev) ?? {}
									: {}),
							}));
							onSearch();
						}}
						{...filterItem.checkboxGroupProps}
					/>
				);
			case EEditableFilterType.AUTOCOMPLETE:
				return (
					<AutoComplete
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						label={label}
						inputClassName="!w-[200px]"
						searchValue={formik.values[value]}
						value={formik.values[value]}
						onChange={(e) => {
							formik.setValues((prev) => ({
								...prev,
								[value]: e,
								...(getAdditionalValues
									? getAdditionalValues?.(e, prev) ?? {}
									: {}),
							}));
						}}
						onSelect={onSearch}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								onSearch();
							}
						}}
						{...filterItem?.autoCompleteProps}
					/>
				);
			case EEditableFilterType.INPUT_TARGET:
				return (
					<InputTarget
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						name={value}
						formik={formik}
						inputProps={{ ...filterItem.inputProps, onPressEnter: onSearch }}
						dropdownProps={{
							...filterItem.dropdownProps,
							onSelect: onSearch,
						}}
						autoCompleteProps={filterItem?.autoCompleteProps}
						searchTypeConfigs={filterItem.searchTypeConfigs}
						onSearch={onSearch}
					/>
				);
			case EEditableFilterType.INPUT_CHECKBOX:
				return (
					<InputCheckbox
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						name={value}
						formik={formik}
						inputProps={{ ...filterItem.inputProps, onPressEnter: onSearch }}
						checkboxProps={{
							yes: {
								onClick: onSearch,
							},
							no: {
								onClick: onSearch,
							},
						}}
					/>
				);
			default:
				return (
					<Input
						label={label}
						labelClassName={labelClassName}
						containerClassName={containerClassName}
						inputClassName="!w-[200px]"
						name={value}
						value={formik.values[value]}
						onChange={(e) =>
							formik.setValues((prev: any) => ({
								...prev,
								[value]: e.target.value,
								...(getAdditionalValues
									? getAdditionalValues?.(e, prev) ?? {}
									: {}),
							}))
						}
						onPressEnter={onSearch}
					/>
				);
		}
	};

	const renderDefaultFilter = useCallback(() => {
		if (isNilOrEmpty(state.defaultOptions)) return null;

		return state.defaultOptions?.map((item) => {
			const children = renderItem(item);
			if (children) {
				return <div key={item.value}>{renderItem(item)}</div>;
			}
		});
	}, [state.defaultOptions, renderItem]);

	const renderFilter = useCallback(() => {
		if (isNilOrEmpty(state.options)) return null;

		return state.options?.map((item, index) => {
			const children = renderItem(item);
			if (children)
				return (
					<div
						key={item.value}
						className="h-[54px] border-b border-collapse flex items-center justify-between first:border-t first:mt-3 w-full"
					>
						<div className="w-full">{renderItem(item)}</div>
						<div className="ml-auto flex items-center">
							<div
								className="btn-remove-field cursor-pointer"
								onClick={() => handleRemoveFilter(item)}
							>
								<CloseSquareOutlined />
							</div>
						</div>
					</div>
				);
		});
	}, [state.options, renderItem, handleRemoveFilter]);

	return (
		<div id="search-filter">
			<div className="card">
				<div className="col gap-y-[10px]">{renderDefaultFilter()}</div>

				<div>{renderFilter()}</div>
				{!hideAdditionalFilter && (
					<div className="mt-3">
						<Select
							label={t?.('add-search-items')}
							labelClassName={labelClassName}
							className="!w-[200px]"
							containerClassName={containerClassName}
							placeholder={t?.('select')}
							value={null}
							options={state?.rootOptions}
							onChange={handleAddFilter}
							id="select-add-field"
						/>
					</div>
				)}

				<Space className="flex justify-center mt-5" size={10}>
					<Button secondary onClick={onSearch} id="search-button" isMainBtn>
						{t?.('search')}
					</Button>
					<Button
						type="default"
						onClick={onClickReset}
						id="reset-button"
						isMainBtn
					>
						{t?.('reset')}
					</Button>
				</Space>
			</div>
		</div>
	);
};

export default memo(forwardRef(SearchFilter));
